import { IonHeader, IonIcon, IonModal, IonToolbar } from '@ionic/react';
import { chevronBackOutline } from 'ionicons/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';

const MobileModalContent = styled.div`
  padding: 20px;
  height: fit-content;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  position: relative;
  z-index: 1;
  color: ${({ theme }) => theme.text.regular};
  text-align: center;
  background-color: ${({ theme }) => theme.background.primary};
`;

const MobileModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const BackButton = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: ${({ theme }) => theme.text.regular};
  font-size: 14px;
  position: relative;
  z-index: 2;
`;

const IonicModal = ({
  children,
  onClose,
  onBack,
  title,
  width,
  customStyle,
  customOverlayStyle = {},
  header,
  onDidPresent = () => {},
}) => {
  const theme = useTheme();
  const [breakpoints, setBreakpoints] = useState([0]);
  const [initialBreakpoint, setInitialBreakpoint] = useState(0);

  const initialContentRef = useRef(null);
  const finalContentRef = useRef(null);

  const updateModalBreakpoint = useCallback((contentRef, initialBreakpoint) => {
    if (!contentRef.current) return;

    const contentHeight = contentRef.current.scrollHeight;

    if (contentHeight === 0) return;

    // Calculate available viewport height by subtracting safe area insets
    const safeAreaTop = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue(
        '--ion-safe-area-top',
      ) || '0',
    );
    const safeAreaBottom = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue(
        '--ion-safe-area-bottom',
      ) || '0',
    );
    const availableHeight = window.innerHeight - safeAreaTop - safeAreaBottom;

    const fullBreakpoint = Math.min(contentHeight / availableHeight, 1);

    const modalEl = document.querySelector('ion-modal');

    if (modalEl) {
      modalEl.breakpoints = [0, fullBreakpoint];
      modalEl.setCurrentBreakpoint(fullBreakpoint);
    }
    setBreakpoints([0, fullBreakpoint]);
    setInitialBreakpoint(fullBreakpoint);
  }, []);

  useEffect(() => {
    const measureInitialContent = () => {
      if (!initialContentRef.current || initialBreakpoint !== 0) return;

      const contentHeight = initialContentRef.current.scrollHeight;

      if (contentHeight === 0) {
        requestAnimationFrame(measureInitialContent);
        return;
      }

      updateModalBreakpoint(initialContentRef);
    };

    requestAnimationFrame(measureInitialContent);
  }, [initialContentRef.current, updateModalBreakpoint, initialBreakpoint]);

  useEffect(() => {
    if (
      !finalContentRef.current ||
      initialBreakpoint === 0 ||
      initialBreakpoint === 1
    )
      return;

    const resizeObserver = new ResizeObserver(() => {
      updateModalBreakpoint(finalContentRef, initialBreakpoint);
    });

    resizeObserver.observe(finalContentRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [finalContentRef.current, updateModalBreakpoint, initialBreakpoint]);

  // Handle input focus and keyboard alignment
  useEffect(() => {
    if (initialBreakpoint === 0) return;

    const handleInputFocus = (e) => {
      const target = e.target;
      // Only handle input, textarea, and contenteditable elements
      if (!target || typeof target.tagName !== 'string') return;

      const isInput = target.tagName === 'INPUT';
      const isTextarea = target.tagName === 'TEXTAREA';
      const isContentEditable = target.isContentEditable === true;

      if (isInput || isTextarea || isContentEditable) {
        // Small delay to ensure keyboard animation starts
        setTimeout(() => {
          try {
            if (target && typeof target.scrollIntoView === 'function') {
              target.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'nearest',
              });
            }
          } catch (error) {
            // Silently fail if scrollIntoView is not available or fails
            console.warn('Failed to scroll input into view:', error);
          }
        }, 100);
      }
    };

    const contentElement = finalContentRef.current;
    if (contentElement) {
      contentElement.addEventListener('focusin', handleInputFocus, true);
      return () => {
        contentElement.removeEventListener('focusin', handleInputFocus, true);
      };
    }
  }, [initialBreakpoint]);

  if (initialBreakpoint === 0) {
    return (
      <MobileModalContent ref={initialContentRef} style={{ zIndex: -1000 }}>
        {(onBack || title) && (
          <MobileModalHeader>
            {onBack && (
              <BackButton
                onClick={(e) => {
                  e.stopPropagation();
                  onBack();
                }}
              >
                <IonIcon icon={chevronBackOutline} />
                Back
              </BackButton>
            )}
            {title && !onBack && <div />}
          </MobileModalHeader>
        )}
        {children}
      </MobileModalContent>
    );
  }

  return (
    <IonModal
      id='ion-modal'
      isOpen={true}
      onDidDismiss={onClose}
      onDidPresent={onDidPresent}
      breakpoints={breakpoints}
      initialBreakpoint={initialBreakpoint}
      keyboardResize="body"
      style={{
        '--background': theme.background.primary,
        '--width': width || '100%',
      }}
    >
      {header && (
        <IonHeader>
          <IonToolbar style={{ '--background': theme.background.primary }}>
            {header}
          </IonToolbar>
        </IonHeader>
      )}
      <MobileModalContent ref={finalContentRef}>
        {(onBack || title) && (
          <MobileModalHeader>
            {onBack && (
              <BackButton
                onClick={(e) => {
                  e.stopPropagation();
                  onBack();
                }}
              >
                <IonIcon icon={chevronBackOutline} />
                Back
              </BackButton>
            )}
            {title && !onBack && <div />}
          </MobileModalHeader>
        )}
        {children}
      </MobileModalContent>
    </IonModal>
  );
};

export default IonicModal;
